import React, { useState, useEffect, useRef, useCallback } from "react";

const POLL_INTERVAL = 10000;

const GenerationQueuePanel = ({ isAdmin = false, onComplete }) => {
  const [queueData, setQueueData] = useState({ in_progress: [], pending: [] });
  const [visible, setVisible] = useState(false);
  const prevQueueDataRef = useRef({ in_progress: [], pending: [] });

  const fetchQueueStatus = useCallback(async () => {
    try {
      const response = await fetch("/api/google_ads_ai/reports/queue_status", { headers: { Accept: "application/json" } });
      if (response.ok) {
        const data = await response.json();
        const prevData = prevQueueDataRef.current;
        const hadItems = prevData.in_progress.length > 0 || prevData.pending.length > 0;
        const hasItems = data.in_progress.length > 0 || data.pending.length > 0;
        setQueueData(data);
        setVisible(hasItems);
        prevQueueDataRef.current = data;
        if (hadItems && !hasItems && onComplete) onComplete();
      }
    } catch (err) {
      console.error("[GoogleAdsAI GenerationQueuePanel] Error fetching queue status:", err);
    }
  }, [onComplete]);

  // Polling disabled for now to avoid repeated queue_status requests
  // useEffect(() => {
  //   fetchQueueStatus();
  //   const id = setInterval(fetchQueueStatus, POLL_INTERVAL);
  //   return () => clearInterval(id);
  // }, [fetchQueueStatus]);

  const refresh = useCallback(() => { fetchQueueStatus(); }, [fetchQueueStatus]);
  useEffect(() => {
    window.__googleAdsGenerationQueueRefresh = refresh;
    return () => { delete window.__googleAdsGenerationQueueRefresh; };
  }, [refresh]);

  const handleCancel = async (reportId) => {
    if (!window.confirm("Are you sure you want to cancel this generation? This cannot be undone.")) return;
    try {
      const response = await fetch(`/api/google_ads_ai/reports/${reportId}/cancel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content"),
        },
      });
      if (response.ok) fetchQueueStatus();
    } catch (err) {
      console.error("[GoogleAdsAI GenerationQueuePanel] Cancel error:", err);
    }
  };

  if (!visible) return null;

  const { in_progress, pending } = queueData;

  return (
    <div className="generation-queue-panel">
      <div className="generation-queue-panel__header">
        <span className="generation-queue-panel__title">Google Ads Generation Queue</span>
      </div>
      {in_progress.map((item) => (
        <RunningItem key={item.id} item={item} isAdmin={isAdmin} onCancel={handleCancel} />
      ))}
      {pending.length > 0 && (
        <div className="generation-queue-panel__pending-section">
          <div className="generation-queue-panel__pending-label">QUEUED</div>
          {pending.map((item, idx) => (
            <PendingItem key={item.id} item={item} position={idx + 1} onCancel={handleCancel} />
          ))}
        </div>
      )}
    </div>
  );
};

const RunningItem = ({ item, isAdmin, onCancel }) => {
  const [elapsed, setElapsed] = useState(item.elapsed_seconds || 0);
  useEffect(() => {
    setElapsed(item.elapsed_seconds || 0);
    const timer = setInterval(() => setElapsed((prev) => prev + 1), 1000);
    return () => clearInterval(timer);
  }, [item.elapsed_seconds, item.id]);
  const formatElapsed = (s) => `${Math.floor(s / 60)}m ${(s % 60).toString().padStart(2, "0")}s`;
  return (
    <div className="generation-queue-item generation-queue-item--running">
      <div className="generation-queue-item__main">
        <div className="generation-queue-item__status-indicator" />
        <div className="generation-queue-item__info">
          <div className="generation-queue-item__event-name">{item.event_name || "Unknown Event"}</div>
          <div className="generation-queue-item__details">
            <span className="generation-queue-item__stage">{item.stage_label}</span>
            <span className="generation-queue-item__separator">|</span>
            <span className="generation-queue-item__elapsed">{formatElapsed(elapsed)}</span>
          </div>
        </div>
        <button type="button" className="generation-queue-item__cancel" onClick={() => onCancel(item.id)} title="Cancel generation">Cancel</button>
      </div>
    </div>
  );
};

const PendingItem = ({ item, position, onCancel }) => (
  <div className="generation-queue-item generation-queue-item--pending">
    <div className="generation-queue-item__main">
      <div className="generation-queue-item__position">{position}</div>
      <div className="generation-queue-item__info">
        <div className="generation-queue-item__event-name">{item.event_name || "Unknown Event"}</div>
      </div>
      <button type="button" className="generation-queue-item__cancel" onClick={() => onCancel(item.id)} title="Remove from queue">Cancel</button>
    </div>
  </div>
);

export default GenerationQueuePanel;
